import React, { useState, useEffect, useRef, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { Card } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { Table, Empty, Badge, Select, Input, Drawer, Badge as AntBadge, Tooltip } from "antd";
import { SearchOutlined, ReloadOutlined, FilterOutlined, CheckCircleOutlined, TagOutlined, CreditCardOutlined, GlobalOutlined, EnvironmentOutlined, AppstoreOutlined } from "@ant-design/icons";
import moment from "moment";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import misc from "../../../api/misc/miscService";
import type {
  PricingListItem,
  AppDropdownItem,
  Country,
  State,
  PaginationState,
} from "../../../types/super-admin";

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const PricingList: React.FC = () => {
  const navigate = useNavigate();
  const [onSearchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(onSearchText, 300);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
    sortOrder: "DESC" as "ASC" | "DESC",
    direction: "P" as "P" | "F" | "B",
    pageDiff: 0,
    paginationId: null as number | null,
  });

  const [dataSource, setDataSource] = useState<PricingListItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Filter state
  const [filterApp, setFilterApp] = useState<number | undefined>(
    undefined
  );
  const [filterPricingType, setFilterPricingType] = useState<
    string | undefined
  >(undefined);
  const [filterBilling, setFilterBilling] = useState<string | undefined>(
    undefined
  );
  const [filterCountry, setFilterCountry] = useState<number | undefined>(
    undefined
  );
  const [filterState, setFilterState] = useState<number | undefined>(undefined);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Dropdown options
  const [appOptions, setAppOptions] = useState<AppDropdownItem[]>([]);
  const [countryOptions, setCountryOptions] = useState<Country[]>([]);
  const [stateOptions, setStateOptions] = useState<State[]>([]);

  // Fetch filter dropdown options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      const results = await Promise.allSettled([
        superAdminService.getAppDropdown(),
        misc.getCountries(),
      ]);

      if (results[0].status === "fulfilled") {
        const appRes = results[0].value;
        if (appRes.status && appRes.data) {
          setAppOptions(
            Array.isArray(appRes.data) ? appRes.data : []
          );
        }
      }
      if (results[1].status === "fulfilled") {
        const countryRes = results[1].value;
        if (countryRes.status && countryRes.data) {
          setCountryOptions(
            Array.isArray(countryRes.data) ? countryRes.data : []
          );
        }
      }
    };
    fetchFilterOptions();
  }, []);

  // Cascade: fetch states when country changes
  useEffect(() => {
    if (filterCountry) {
      const fetchStates = async () => {
        try {
          const res = await misc.getStatesByCountry(filterCountry);
          if (res.status && res.data) {
            setStateOptions(Array.isArray(res.data) ? res.data as State[] : []);
          }
        } catch (error) {
          console.log("Error fetching states:", error);
        }
      };
      fetchStates();
    } else {
      setStateOptions([]);
      setFilterState(undefined);
    }
  }, [filterCountry]);

  const buildFilters = useCallback(() => {
    const filters: any = {};
    if (filterApp) filters.appId = filterApp;
    if (filterPricingType) filters.pricingType = filterPricingType;
    if (filterBilling) filters.billingFrequency = filterBilling;
    if (filterCountry) filters.countryId = filterCountry;
    if (filterState) filters.stateId = filterState;
    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [filterApp, filterPricingType, filterBilling, filterCountry, filterState]);

  const fetchData = async (params: any = {}) => {
    setLoading(true);
    try {
      const filters = params.filters !== undefined ? params.filters : buildFilters();
      const apiParams: any = {
        sortOrder: params.sortOrder || pagination.sortOrder,
        direction: params.direction || pagination.direction,
        pageSize: params.pageSize || pagination.pageSize,
        pageDiff: params.pageDiff ?? pagination.pageDiff,
        paginationId: params.paginationId ?? pagination.paginationId,
        searchQuery:
          params.searchQuery !== undefined
            ? params.searchQuery
            : onSearchText,
      };

      // Spread filter params at the top level for query string
      if (filters) {
        if (filters.appId) apiParams["filters[appId]"] = filters.appId;
        if (filters.pricingType) apiParams["filters[pricingType]"] = filters.pricingType;
        if (filters.billingFrequency) apiParams["filters[billingFrequency]"] = filters.billingFrequency;
        if (filters.countryId) apiParams["filters[countryId]"] = filters.countryId;
        if (filters.stateId) apiParams["filters[stateId]"] = filters.stateId;
      }

      const { data, status, message } =
        await superAdminService.getPricingList(apiParams);

      if (status) {
        const list = Array.isArray(data) ? data : data?.list || [];
        const count = Array.isArray(data) ? data.length : data?.count || 0;
        setDataSource(list);
        setPagination((prev) => ({
          ...prev,
          total: count,
          ...(params.current && { current: params.current }),
          ...(params.pageSize && { pageSize: params.pageSize }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
          ...(params.direction && { direction: params.direction }),
          ...(params.pageDiff !== undefined && { pageDiff: params.pageDiff }),
          ...(params.paginationId !== undefined && {
            paginationId: params.paginationId,
          }),
        }));
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    });
  }, []);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
      searchQuery: debouncedSearch.trim(),
    });
  }, [debouncedSearch]);

  const tableOnChange = async (
    newPagination: any,
    _filters: any,
    _sorter: any
  ) => {
    const apiParams: any = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    };

    if (newPagination.pageSize !== pagination.pageSize) {
      apiParams.pageDiff = Math.floor(
        ((pagination.current - 1) * pagination.pageSize) /
          newPagination.pageSize
      );
    } else if (newPagination.current !== pagination.current) {
      apiParams.direction =
        newPagination.current > pagination.current ? "F" : "B";
      apiParams.pageDiff =
        Math.abs(newPagination.current - pagination.current) - 1;

      if (apiParams.direction === "F" && dataSource.length > 0) {
        apiParams.paginationId =
          dataSource[dataSource.length - 1]?.paginationId || dataSource[dataSource.length - 1]?.modulePriceId;
      } else if (apiParams.direction === "B" && dataSource.length > 0) {
        apiParams.paginationId = dataSource[0]?.paginationId || dataSource[0]?.modulePriceId;
      }
    }

    await fetchData(apiParams);
  };

  const handleClearFilters = () => {
    setFilterApp(undefined);
    setFilterPricingType(undefined);
    setFilterBilling(undefined);
    setFilterCountry(undefined);
    setFilterState(undefined);
  };

  // Auto-apply filters when any filter changes
  const filtersInitialized = useRef(false);

  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    });
  }, [filterApp, filterPricingType, filterBilling, filterCountry, filterState]);

  const formatCurrency = (amount: number, currencyCode: string) => {
    const localeMap: Record<string, string> = { INR: "en-IN", USD: "en-US", EUR: "de-DE", GBP: "en-GB" };
    try {
      return new Intl.NumberFormat(localeMap[currencyCode] || "en-US", {
        style: "currency",
        currency: currencyCode,
      }).format(amount);
    } catch {
      return `${currencyCode} ${amount.toFixed(2)}`;
    }
  };

  const pricingTypeColor: Record<string, string> = {
    Flat: "blue",
    "User-based": "cyan",
  };

  const columns = [
    {
      title: "Pricing Name",
      dataIndex: "pricingName",
      key: "pricingName",
      width: 270,
      render: (text: string, record: PricingListItem) => (
        <Link
          to={`/sa-pricing/${record.modulePriceId}`}
          className="text-primary fw-bold"
        >
          {text || "-"}
        </Link>
      ),
    },
    {
      title: "Application",
      dataIndex: "appName",
      key: "appName",
      width: 240,
    },
    {
      title: "Pricing Type",
      dataIndex: "pricingType",
      key: "pricingType",
      width: 130,
      render: (type: string) => (
        <Badge color={pricingTypeColor[type] || "default"}>{type}</Badge>
      ),
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 150,
      render: (val: number | null, record: any) =>
        val != null && record.currencyCode
          ? formatCurrency(Number(val), record.currencyCode)
          : val != null ? Number(val).toFixed(2) : "-",
    },
    {
      title: "Billing",
      dataIndex: "billingFrequency",
      key: "billingFrequency",
      width: 100,
    },
    {
      title: "Country",
      dataIndex: "countryName",
      key: "countryName",
      width: 120,
      render: (text: string | null) => text || "Global",
    },
    {
      title: "State",
      dataIndex: "stateName",
      key: "stateName",
      width: 120,
      render: (text: string | null) => text || "-",
    },
    {
      title: "Valid From",
      dataIndex: "validFrom",
      key: "validFrom",
      width: 120,
      render: (date: string) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: "Valid To",
      dataIndex: "validTo",
      key: "validTo",
      width: 120,
      render: (date: string) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (isActive: boolean) => (
        <span
          className={`tb-status text-${isActive ? "success" : "danger"}`}
        >
          {isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Customers",
      dataIndex: "linkedCustomersCount",
      key: "linkedCustomersCount",
      width: 100,
      render: (val: number) => val || 0,
    },
  ];

  const hasActiveFilters =
    filterApp || filterPricingType || filterBilling || filterCountry || filterState;

  const activeFilterCount = [filterApp, filterPricingType, filterBilling, filterCountry, filterState].filter(Boolean).length;

  return (
    <React.Fragment>
      <Head title="SaaS Pricing" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                SaaS Pricing
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>You have total {pagination.total} pricing configurations.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                onClick={() => navigate("/sa-pricing/create")}
              >
                <Icon name="plus"></Icon>
                <span>Add</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner py-3">
              <div className="d-flex justify-content-between align-items-center">
                <Input
                  prefix={<SearchOutlined style={{ color: "#b0b0b0" }} />}
                  placeholder="Search by name, application, currency..."
                  value={onSearchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  variant="borderless"
                  style={{ width: 570, borderBottom: "1.5px solid #e0e0e0", borderRadius: 0 }}
                  allowClear
                />
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <Tooltip title="Refresh">
                    <span
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: "50%", cursor: "pointer", transition: "background 0.2s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      onClick={() => {
                        hasFetched.current = false;
                        fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
                      }}
                    >
                      <ReloadOutlined style={{ fontSize: 18 }} />
                    </span>
                  </Tooltip>
                  <Tooltip title="Filters">
                    <AntBadge count={activeFilterCount} size="small" offset={[-4, 4]}>
                      <span
                        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: "50%", cursor: "pointer", transition: "background 0.2s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        onClick={() => setFilterDrawerOpen(true)}
                      >
                        <FilterOutlined style={{ fontSize: 18 }} />
                      </span>
                    </AntBadge>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className="card-inner p-0">
              {loading ? (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                      {["Pricing Name", "Application", "Pricing Type", "Unit Price", "Billing", "Country", "State", "Valid From", "Valid To", "Status", "Customers"].map((h) => (
                        <th key={h} style={{ padding: "11px 12px", fontSize: 12, fontWeight: 600, color: "#8c8c8c", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(10)].map((_, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 180, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 150, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 80, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 70, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 65, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 80, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 75, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 85, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 85, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 55, height: 22, borderRadius: 4 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 40, height: 14 }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : dataSource.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  rowKey="modulePriceId"
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '15', '20', '50', '100', '500'],
                  }}
                  onChange={tableOnChange}
                  scroll={{ x: 1600  }}
                  bordered
                  size="small"
                />
              ) : (
                <Empty description="No pricing configurations found" />
              )}
            </div>
          </Card>
        </Block>
      </Content>

      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FilterOutlined style={{ fontSize: 16, color: "#6576ff" }} />
            <span style={{ fontSize: 16, fontWeight: 700 }}>Filters</span>
            {activeFilterCount > 0 && (
              <AntBadge count={activeFilterCount} style={{ backgroundColor: "#6576ff", marginLeft: 4 }} />
            )}
          </div>
        }
        placement="right"
        onClose={() => setFilterDrawerOpen(false)}
        open={filterDrawerOpen}
        width={450}
        zIndex={1060}
        extra={
          hasActiveFilters ? (
            <span style={{ fontSize: 13, color: "#ff4d4f", cursor: "pointer", fontWeight: 600 }} onClick={handleClearFilters}>
              Reset All
            </span>
          ) : null
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)", border: "1px solid #d3adf7" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <AppstoreOutlined style={{ color: "#722ed1", fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Application</span>
            </div>
            <Select
              placeholder="Select application"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              value={filterApp}
              onChange={(val) => setFilterApp(val)}
              options={appOptions.map((a) => ({
                label: a.appName,
                value: a.appId,
              }))}
            />
          </div>
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #f6ffed 0%, #fcffe6 100%)", border: "1px solid #d9f7be" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <TagOutlined style={{ color: "#52c41a", fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Pricing Type</span>
            </div>
            <Select
              placeholder="Select pricing type"
              allowClear
              style={{ width: "100%" }}
              value={filterPricingType}
              onChange={(val) => setFilterPricingType(val)}
              options={[
                { label: "Flat", value: "Flat" },
                { label: "User-based", value: "User-based" },
              ]}
            />
          </div>
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #fff7e6 0%, #fff1f0 100%)", border: "1px solid #ffd591" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <CreditCardOutlined style={{ color: "#fa8c16", fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Billing</span>
            </div>
            <Select
              placeholder="Select billing frequency"
              allowClear
              style={{ width: "100%" }}
              value={filterBilling}
              onChange={(val) => setFilterBilling(val)}
              options={[{ label: "Monthly", value: "Monthly" }]}
            />
          </div>
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)", border: "1px solid #d3adf7" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <GlobalOutlined style={{ color: "#722ed1", fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Country</span>
            </div>
            <Select
              placeholder="Select country"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              value={filterCountry}
              onChange={(val) => {
                setFilterCountry(val);
                setFilterState(undefined);
              }}
              options={countryOptions.map((c) => ({
                label: c.countryName,
                value: c.countryId,
              }))}
            />
          </div>
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)", border: "1px solid #d3adf7" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <EnvironmentOutlined style={{ color: "#722ed1", fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>State</span>
            </div>
            <Select
              placeholder={filterCountry ? "Select state" : "Select country first"}
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              value={filterState}
              onChange={(val) => setFilterState(val)}
              options={stateOptions.map((s) => ({
                label: s.stateName,
                value: s.stateId,
              }))}
              disabled={!filterCountry}
            />
          </div>
        </div>
      </Drawer>
    </React.Fragment>
  );
};

export default PricingList;
